import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  authService,
  LoginCredentials,
  LoginResponse,
} from "../services/auth.service";

interface AuthContextType {
  isAuthenticated: boolean;
  user: LoginResponse["user"] | null;
  entityID: number | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<LoginResponse["user"] | null>(null);
  const [entityID, setEntityID] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };

  useEffect(() => {
    // Check for existing auth token and validate it hasn't expired
    const token = localStorage.getItem("authToken");
    const savedUser = localStorage.getItem("user");
    const savedEntityID = localStorage.getItem("entityID");

    if (token && savedUser && !isTokenExpired(token)) {
      setIsAuthenticated(true);
      setUser(JSON.parse(savedUser));
      setEntityID(savedEntityID ? parseInt(savedEntityID) : null);
    } else if (token && isTokenExpired(token)) {
      // Clear stale auth data
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("entityID");
    }
    setLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const response = await authService.login(credentials);

    localStorage.setItem("authToken", response.token);
    localStorage.setItem("user", JSON.stringify(response.user));
    localStorage.setItem("entityID", response.entityID.toString());

    setIsAuthenticated(true);
    setUser(response.user);
    setEntityID(response.entityID);
  };

  const logout = () => {
    authService.logout().catch(() => {
      // Ignore errors, clear local state anyway
    });

    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("entityID");

    setIsAuthenticated(false);
    setUser(null);
    setEntityID(null);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, entityID, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
